import React, { useEffect, useMemo, useRef, useState } from "react";
import { throttle } from "lodash";
import ClubLogo from "../ClubLogo";
import ClubName from "../ClubName";
import Picture from "../Picture";
import { getFirstCharacter, round } from "../../../../libraries/_helpers";
import { formatImageUrl } from "../../utils";
import LiveStandingsIcon from "../../svg/LiveStandingsIcon";
import IndicatorDown from "../../svg/IndicatorDown";
import IndicatorUp from "../../svg/IndicatorUp";
import { getMatchColumnsLength } from "../../../mls-standings-form-guide/utils";
import { useReactAppState } from "../../../shared/containers/utils";
import ReactTooltip from 'react-tooltip';
import dayjs from "dayjs";
import { trackPlayerClick } from "../../../../libraries/_modules/tracking/snowplow/trackPlayerInteraction";

export function customSort(a, b, accessor) {
  if (a.original[accessor] === b.original[accessor]) {
    return a.original.rank > b.original.rank ? -1 : 1;
  }
  return a.original[accessor] > b.original[accessor] ? 1 : -1;
}

export function sortByLastName(a, b, accessor) {
  return a.original?.lastName > b.original?.lastName ? 1 : -1;
}

export function sortClubNames(a, b, accessor) {
  return a.original[accessor].name > b.original[accessor].name ? 1 : -1;
}

export function getMinimumCount(targetValue, nextValue, defaultValue) {
  const value = targetValue ? targetValue : defaultValue;

  if (nextValue && nextValue !== 0 && value > nextValue) {
    return nextValue;
  }

  return value;
}

export function getPlayoffCounter(headerPure, east, west, central) {
  if (!headerPure) {
    return null;
  }
  if (headerPure?.toLowerCase().includes("west") && west) {
    return west;
  } else if (headerPure?.toLowerCase().includes("east") && east) {
    return east;
  } else if (headerPure?.toLowerCase().includes("central") && central) {
    return central;
  } else {
    return null;
  }
}

export function getStandingsConfig(topClubs, homeAdv, playoffQualified, tableIsGrouped) {
  const classStr = [];
  if (homeAdv > 0 && tableIsGrouped) {
    homeAdv === 1
      ? classStr.push('mls-o-table__row--home-adv mls-o-table__row--home-adv-end')
      : classStr.push('mls-o-table__row--home-adv');
  }

  if (playoffQualified > 0 && tableIsGrouped) {
    playoffQualified === 1
      ? classStr.push('mls-o-table__row--playoff-qualified mls-o-table__row--playoff-qualified-end')
      : classStr.push('mls-o-table__row--playoff-qualified');
  }

  if (topClubs > 0 && tableIsGrouped) {
    topClubs === 1
      ? classStr.push('mls-o-table__row--top-clubs mls-o-table__row--top-clubs-end')
      : classStr.push('mls-o-table__row--top-clubs');
  }

  return classStr.join(" ");
}

export function useHorizontalScrollSpy(dependencies) {
  const [isOriginalPosition, setIsOriginalPosition] = useState(true);
  const [isScrollableRight, setIsScrollableRight] = useState(false);

  const ref = useRef(null);

  useEffect(() => {
    function getScrollLeftPosition() {
      const maxScrollWidth = ref.current.scrollWidth - ref.current.clientWidth;
      // eslint-disable-next-line no-unused-expressions
      ref.current.scrollLeft > 0
        ? setIsOriginalPosition(false)
        : setIsOriginalPosition(true);

      maxScrollWidth === 0 || ref?.current.scrollLeft === maxScrollWidth
        ? setIsScrollableRight(false)
        : setIsScrollableRight(true);
    }

    ref.current.addEventListener('scroll', throttle(getScrollLeftPosition, 100, { trailing: true, leading: true }));

    getScrollLeftPosition();

    return function cleanUp() {
      ref?.current?.removeEventListener('scroll', throttle(getScrollLeftPosition, 100, { trailing: true }));
    };
  }, [dependencies, ref]);

  return [ref, isOriginalPosition, isScrollableRight];
}

export function useColumns(tableType, data) {
  const columnType = getColumnType(tableType, data);

  return useMemo(
    () => [columnType],
    [tableType]
  );
}

export const statTypes = {
  standings: "STANDINGS",
  statsGeneral: "STATS_GENERAL",
  statsPassing: "STATS_PASSING",
  statsAttacking: "STATS_ATTACKING",
  statsDefending: "STATS_DEFENDING",
  statsGoalKeeping: "STATS_GOALKEEPING",
  statsGeneralAllYears: "STATS_GENERAL_ALL_YEARS",
  statsPassingAllYears: "STATS_PASSING_ALL_YEARS",
  statsAttackingAllYears: "STATS_ATTACKING_ALL_YEARS",
  statsDefendingAllYears: "STATS_DEFENDING_ALL_YEARS",
  statsGoalKeepingAllYears: "STATS_GOALKEEPING_ALL_YEARS",
  clubStatsGeneral: "CLUB_STATS_GENERAL",
  clubStatsPassing: "CLUB_STATS_PASSING",
  clubStatsAttacking: "CLUB_STATS_ATTACKING",
  clubStatsDefending: "CLUB_STATS_DEFENDING",
  clubStatsGoalKeeping: "CLUB_STATS_GOALKEEPING",
  clubStatsGeneralPostseason: "CLUB_STATS_GENERAL_POSTSEASON",
  clubStatsGeneralNoStandings: "CLUB_STATS_GENERAL_NO_STANDINGS",
  clubStatsPassingPostseason: "CLUB_STATS_PASSING_POSTSEASON",
  clubStatsAttackingPostseason: "CLUB_STATS_ATTACKING_POSTSEASON",
  clubStatsDefendingPostseason: "CLUB_STATS_DEFENDING_POSTSEASON",
  clubStatsGoalKeepingPostseason: "CLUB_STATS_GOALKEEPING_POSTSEASON",
  clubStatsGeneralAllYears: "CLUB_STATS_GENERAL_ALL_YEARS",
  clubStatsPassingAllYears: "CLUB_STATS_PASSING_ALL_YEARS",
  clubStatsAttackingAllYears: "CLUB_STATS_ATTACKING_ALL_YEARS",
  clubStatsDefendingAllYears: "CLUB_STATS_DEFENDING_ALL_YEARS",
  matchHubPlayerStats: "MATCH_HUB_PLAYER_STATS",
  matchHubGoalkeeperStats: "MATCH_HUB_GOALKEEPER_STATS",
  activeRoster: "ACTIVE_ROSTER",
  activeRosterFiltered: "ACTIVE_ROSTER_FILTERED",
  playersHubRoster: "PLAYERS_HUB_ROSTER",
  playersMatchLogGeneral: "PLAYERS_MATCH_LOG_GENERAL",
  playersMatchLogAttacking: "PLAYERS_MATCH_LOG_ATTACKING",
  playersMatchLogPassing: "PLAYERS_MATCH_LOG_PASSING",
  playersMatchLogDefending: "PLAYERS_MATCH_LOG_DEFENDING",
  playersMatchLogGoalKeeping: "PLAYERS_MATCH_LOG_GOALKEEPING",
  playersCareerStatsGeneral: "PLAYERS_CAREERS_STATS_GENERAL",
  playersCareerStatsAttacking: "PLAYERS_CAREERS_STATS_ATTACKING",
  playersCareerStatsPassing: "PLAYERS_CAREERS_STATS_PASSING",
  playersCareerStatsDefending: "PLAYERS_CAREERS_STATS_DEFENDING",
  playersCareerStatsGoalKeeping: "PLAYERS_CAREERS_STATS_GOALKEEPING",
  formGuide: "FORM_GUIDE",
};

export const tableTypes = {
  clubs: "clubs",
  player: "players"
};

export const phaseTypes = {
  postSeason: "Postseason",
  regularSeason: "Regular Season"
};

export function getClinchedValues(references, data) {
  const items = data?.items || [];

  if (references && items) {
    for (let i = 0; i < items.length; i++) {
      const club = items[i];
      const sportecId = club?.club_id;

      const clinched_x = references.List_of_Clubs_Clinched_x &&
        references.List_of_Clubs_Clinched_x.find(clinchedClub => clinchedClub.fields.sportecId === sportecId) ? "x" : "";
      const clinched_e = references.List_of_Clubs_Clinched_e &&
        references.List_of_Clubs_Clinched_e.find(clinchedClub => clinchedClub.fields.sportecId === sportecId) ? "e" : "";
      const clinched_y = references.List_of_Clubs_Clinched_y &&
        references.List_of_Clubs_Clinched_y.find(clinchedClub => clinchedClub.fields.sportecId === sportecId) ? "y" : "";
      const clinched_s = references.List_of_Clubs_Clinched_s &&
        references.List_of_Clubs_Clinched_s.find(clinchedClub => clinchedClub.fields.sportecId === sportecId) ? "s" : "";

      const clinches = (clinched_x + clinched_e + clinched_y + clinched_s);

      items[i].club = {
        ...items[i].club,
        clinches
      };
    }
  }
}

export function getFormGuideMatchesColumns(data, matchesLength) {
  const { urlList, t } = useReactAppState();
  const { baseUrl } = urlList || {};

  if (!matchesLength || !data) {
    return null;
  }

  const columns = [];
  for (let i = 0; i < matchesLength; i++) {
    columns.push({
      Header: i + 1,
      id: `form-guide-matches-${i + 1}`,
      accessor: (row) => row.matches[i],
      disableSortBy: true,
      Cell: (props) => {
        const match = props.row.original?.matches[i];
        const competitionSlug = props.row.original?.competitionSlug;
        const season = props.row.original?.season;
        const matchDate = match?.matchDate ? dayjs(match?.matchDate).format("M/DD") : null;
        const resultTypeModifier = match?.resultType === "W" ? "mls-c-standings-form-guide__result-type--winner" :
          match?.resultType == "L" ? "mls-c-standings-form-guide__result-type--loser" :
            match?.resultType == "T" ? "mls-c-standings-form-guide__result-type--tie" : "";

        const composedMatchUrl = match?.slug && competitionSlug && season ? `${baseUrl}/competitions/${competitionSlug}/${season}/matches/${match?.slug}` : "";

        return (
          <div>

            {props.row.original && match ?

              <a className="mls-c-standings-form-guide__match-url" data-for={`form-guide-tooltip-${props.row.index}-${i}`} data-tip={`form-guide-tooltip-${props.row.index}-${i}`} href={composedMatchUrl ? composedMatchUrl : null}>
                {match?.homeFullName && match?.awayFullName && matchDate ?
                  <ReactTooltip id={`form-guide-tooltip-${props.row.index}-${i}`}>
                    {match?.result ?
                      `${match?.homeFullName} ${match?.result} ${match?.awayFullName} - ${matchDate}`
                      : `${match?.homeFullName} ${t("vs")} ${match?.awayFullName} - ${matchDate}`}
                  </ReactTooltip>
                  : null}
                {match?.resultType ?
                  <div className={`mls-c-standings-form-guide__result-type ${resultTypeModifier}`}>{match?.resultType}</div>
                  :
                  <div
                    data-for={`form-guide-tooltip-${props.row.index}-${i}`}
                    data-tip={`form-guide-tooltip-${props.row.index}-${i}`}
                    className="mls-c-standings-form-guide__result-type mls-c-standings-form-guide__result-type--no-value">
                    <span />
                  </div>}
                {match?.awayAbbr && match?.homeAbbr ? <div className="mls-c-standings-form-guide__opponent">{`${props.row.original.abbr === match?.awayAbbr ? `@${match?.homeAbbr}` : match?.awayAbbr}`}</div> :
                  <div className="mls-c-standings-form-guide__opponent mls-c-standings-form-guide__opponent--no-value" />}
                {match?.result ? <div className="mls-c-standings-form-guide__result-value">{match?.result}</div> :
                  <div className="mls-c-standings-form-guide__result-value mls-c-standings-form-guide__result-value--no-value" />}
              </a>

              : <div
                data-for={`form-guide-tooltip-${props.row.index}-${i}`}
                data-tip={`form-guide-tooltip-${props.row.index}-${i}`}
                className="mls-c-standings-form-guide__result-type mls-c-standings-form-guide__result-type--no-value">
                <span />
              </div>}

          </div>
        );
      }
    });
  }

  return columns;
}

export function getColumnType(tableType, data, clubAbbreviation) {
  const { t } = useReactAppState() || {};
  const normalizedType = tableType.toUpperCase();
  switch (normalizedType) {
    case statTypes.formGuide:
      const matchesLength = getMatchColumnsLength(data);
      const formGuideMatchesColumns = matchesLength ? getFormGuideMatchesColumns(data, matchesLength) : [];

      return {
        Header: "form_guide",
        accessor: "form-guide",
        disableSortBy: true,
        columns: [
          {
            accessor: "rank",
            Cell: (props) => {
              return props.value || props.value === 0
                ? (
                  <div className="mls-o-table__rank">
                    <div className="mls-o-table__rank-wrapper">
                      <span className="mls-o-table__rank-number">{props.row.original.rank}</span>
                    </div>
                    {
                      props.row.original
                        ?
                        (
                          <span className="mls-o-table__club-logo">
                            <ClubLogo club={props.row.original} element_position="form-guide" />
                          </span>
                        )
                        : null
                    }
                  </div>
                )
                : (
                  <div className="mls-o-table__rank">
                    <div className="mls-o-loading mls-o-loading--glow mls-o-loading--50 mls-o-loading--table-cell" />
                  </div>
                );
            }
          },
          {
            Header: "Club",
            accessor: "abbr",
            Cell: (props) => {
              const abbreviation = props.row.original?.abbr;
              const clubSlug = props.row.original?.slug;

              return props.value || props.value === 0
                ? (
                  <ClubName isAbbreviationOnly abbreviation={abbreviation} slug={clubSlug} />
                )
                :
                <div className="mls-o-table__abbr">
                  <div className="mls-o-loading mls-o-loading--glow mls-o-loading--50 mls-o-loading--table-cell" />
                </div>;
            }
          },
          ...formGuideMatchesColumns
        ]
      };
    case statTypes.standings:
      return {
        Header: data?.header,
        accessor: 'competition-type',
        disableSortBy: true,
        columns: [
          {
            Header: t("rank"),
            accessor: "rank",
            sortInverted: false,
            Cell: (props) => {
              return (
                <div className="mls-o-table__rank">
                  <div className="mls-o-table__rank-wrapper">
                    <span className="mls-o-table__rank-number">{props.row.original.position}</span>
                       {props.row?.original?.tendency ==="up" ? <IndicatorUp extraClasses={"mls-c-standings__indicator"} /> : null}
                       {props.row?.original?.tendency  ==="down" ? <IndicatorDown extraClasses={"mls-c-standings__indicator"} /> : null}                    {/*<span className="mls-o-table__trending"><Chevron/></span>                  */}
                  </div>
                  {props.row.original?.club?.logoColorUrl ?
                    (
                      <span className="mls-o-table__club-logo">
                        <ClubLogo club={props.row.original?.club} element_position="standings" />
                      </span>
                    ) : null
                  }
                </div>
              )
            }
          },
          {
            Header: t("table_club"),
            accessor: "club",
            disableSortBy: true,
            Cell: (props) => {
              const displayName = props?.value?.shortName;
              const abbreviation = props?.value?.abbreviation;
              const clinchedValues = props?.value?.clinches;
              const slug = props?.value?.slug;

              return <>
                {
                  clinchedValues && clinchedValues !== "" ? <span className="mls-o-table__club-clinched">{clinchedValues} - </span> : ""
                }
                <ClubName name={displayName} abbreviation={abbreviation} slug={slug} element_position="standings" />
                {props.row.original?.value?.isLive ? <LiveStandingsIcon size={"medium"} extraClasses="mls-c-standings__live-icon" /> : null}
              </>
            }
          },
          {
            accessor: "points",
            sortInverted: false,
            sortType: (a, b, accessor) => customSort(a, b, accessor),
            Cell: (props) => fallbackTableDataLoaderInteger(props),
            Header: () =>
              <>
                <span className="mls-o-table__name">{t("points")}</span>
                <span className="mls-o-table__abbreviation">PTS</span>
              </>
          },
          getColumnProperties({
            accessor: "points_per_game",
            Header: getColumnHeader("total_points_pg"),
            sortInverted: false,
            customCellFn: fallbackTableDataLoaderString
          }),
          getColumnProperties({
            accessor: "games_played",
            sortInverted: false,
            customCellFn: fallbackTableDataLoaderString
          }),
          getColumnProperties({
            accessor: "wins",
            sortInverted: false,
            customCellFn: fallbackTableDataLoaderString
          }),
          getColumnProperties({
            accessor: "losses",
            sortInverted: false,
            customCellFn: fallbackTableDataLoaderString
          }),
          getColumnProperties({
            accessor: "draws",
            sortInverted: false,
            customCellFn: fallbackTableDataLoaderString
          }),
          getColumnProperties({
            accessor: "goals_scored",
            sortInverted: false,
            customCellFn: fallbackTableDataLoaderString
          }),
          getColumnProperties({
            accessor: "goals_against",
            sortInverted: false,
            customCellFn: fallbackTableDataLoaderString
          }),
          getColumnProperties({
            accessor: "goals_difference",
            sortInverted: false,
            customCellFn: fallbackTableDataLoaderString
          }),
          getColumnProperties({
            accessor: "home_record",
            disableSortBy: true,
            customCellFn: fallbackTableDataLoaderString
          }),
          getColumnProperties({
            accessor: "away_record",
            disableSortBy: true,
            customCellFn: fallbackTableDataLoaderString
          }),
          // getColumnProperties({
          //     Header: "Form", accessor: "form", disableSortBy: true, customCellFn: fallbackTableDataLoaderString
          //   }
          // ),

        ]
      };
    case statTypes.statsPassing:
      return {
        Header: t("passing"),
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            Header: t("table_club"),
            accessor: "club",
            disableSortBy: true,
            customCellFn: fallbackClubTableData
          }),
          getColumnProperties({
            accessor: "games_played",
            customSortFn: null
          }),
          getColumnProperties({
            accessor: "game_started",
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "accurate_pass",
            disableSortBy: false,
          }),
          getColumnProperties({
            accessor: "total_pass",
          }),
          getColumnProperties({
            accessor: "accurate_pass_per"
          }),
          getColumnProperties({
            accessor: "total_att_assist"
          }),
          getColumnProperties({
            accessor: "corner_taken",
          }),
          getColumnProperties({
            accessor: "accurate_cross"
          }),
          getColumnProperties({
            accessor: "goal_assist",
            disableSortBy: false,
          }),
          getColumnProperties({
            accessor: "assist_90",
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: getColumnHeader("accurate_long_balls"),
            accessor: "accurate_long_balls"
          }),
          getColumnProperties({
            accessor: "successful_short_pass"
          }),
          getColumnProperties({
            accessor: "accurate_cross_per",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "att_freekick_total",
            disableSortBy: true
          }),
        ]
      };
    case statTypes.statsDefending:
      return {
        Header: "defending",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            Header: t("table_club"),
            accessor: "club",
            disableSortBy: true,
            customCellFn: fallbackClubTableData
          }),
          getColumnProperties({
            accessor: "games_played",
            customSortFn: null
          }),
          getColumnProperties({
            accessor: "game_started",
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "aerial_duel"
          }),
          getColumnProperties({
            accessor: "aerial_duel_per",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "total_clearance"
          }),
          getColumnProperties({
            accessor: "fk_foul_lost",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "interception",
            disableSortBy: true
          }),
        ]
      };
    case statTypes.statsGoalKeeping:
      return {
        Header: "goalkeeping",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            Header: t("table_club"),
            accessor: "club",
            disableSortBy: true,
            customCellFn: fallbackClubTableData
          }),
          getColumnProperties({
            accessor: "games_played",
            customSortFn: null
          }),
          getColumnProperties({
            accessor: "game_started",
            disableSortBy: true
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "clean_sheet",
            customSortFn: null,
            disableSortBy: false,
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            disableSortBy: false
          }),
          getColumnProperties({
            accessor: "saves",
            disableSortBy: false
          }),
          getColumnProperties({
            accessor: "accurate_pass_per"
          }),
          getColumnProperties({
            accessor: "long_ball_percentage",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "total_clearance"
          }),
        ]
      };
    case statTypes.clubStatsDefendingPostseason:
    case statTypes.clubStatsDefending:
      return {
        Header: "club-defending",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          {
            Header: t("table_club"),
            accessor: "club",
            sortType: (a, b, accessorProperty) => sortClubNames(a, b, accessorProperty),
            Cell: (props) => {
              const displayName = props.row.original.club?.shortName || props.row.original.club?.fullName;
              const abbreviation = props.row.original.club?.abbreviation;

              return props.value || props.value === 0
                ? (
                  <>
                    {
                      props.row.original?.club
                        ?
                        (
                          <span className="mls-o-table__club-logo">
                            <ClubLogo club={props.row.original.club} element_position="club-stats-defending" />
                          </span>
                        )
                        : null
                    }
                    <ClubName name={displayName} abbreviation={abbreviation} slug={props.row.original.club?.slug} />
                  </>
                )
                : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
            }
          },
          getColumnProperties({
            accessor: "goals_conceded",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "shots_against"
          }),
          getColumnProperties({
            accessor: "clean_sheet",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "save_percentage"
          }),
          getColumnProperties({
            accessor: "aerial_duel_per"
          }),
          getColumnProperties({
            accessor: "total_clearance"
          }),
          getColumnProperties({
            accessor: "fk_foul_lost"
          }),
          getColumnProperties({
            accessor: "penalty_conceded"
          }),
          getColumnProperties({
            Header: getColumnHeader("penalty_save"),
            accessor: "penalty_save"
          }),
          getColumnProperties({
            accessor: "penalty_kick_save_percentage"
          }),
        ]
      };
    case statTypes.clubStatsDefendingAllYears:
      return {
        Header: "club-defending",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          {
            Header: t("table_club"),
            accessor: "club",
            sortType: (a, b, accessorProperty) => sortClubNames(a, b, accessorProperty),
            Cell: (props) => {
              const displayName = props.row.original.club?.shortName || props.row.original.club?.fullName;
              const abbreviation = props.row.original.club?.abbreviation;

              return props.value || props.value === 0
                ? (
                  <>
                    {
                      props.row.original?.club
                        ?
                        (
                          <span className="mls-o-table__club-logo">
                            <ClubLogo club={props.row.original.club} element_position="club-stats-defending-all-years" />
                          </span>
                        )
                        : null
                    }
                    <ClubName name={displayName} abbreviation={abbreviation} slug={props.row.original.club?.slug} />
                  </>
                )
                : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
            }
          },
          getColumnProperties({
            accessor: "goals_conceded",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "shots_against",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "clean_sheet",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "save_percentage",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "headed_duel_percentage",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "ground_duel_percentage",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "interception",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "penalty_conceded"
          }),
          getColumnProperties({
            Header: getColumnHeader("penalty_save"),
            accessor: "penalty_save",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "penalty_kick_save_percentage",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
        ]
      };
    case statTypes.clubStatsPassingPostseason:
    case statTypes.clubStatsPassing:
      return {
        Header: "passing",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          {
            Header: t("table_club"),
            accessor: "club",
            sortType: (a, b, accessorProperty) => sortClubNames(a, b, accessorProperty),
            Cell: (props) => {
              const displayName = props.row.original.club?.shortName || props.row.original.club?.fullName;
              const abbreviation = props.row.original.club?.abbreviation;
              return props.value || props.value === 0
                ? (
                  <>
                    {
                      props.row.original?.club
                        ?
                        (
                          <span className="mls-o-table__club-logo">
                            <ClubLogo club={props.row.original.club} element_position="club-stats-passing" />
                          </span>
                        )
                        : null
                    }
                    <ClubName name={displayName} abbreviation={abbreviation} slug={props.row.original.club?.slug} />
                  </>
                )
                : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
            }
          },
          getColumnProperties({
            accessor: "accurate_pass"
          }),
          getColumnProperties({
            accessor: "total_pass"
          }),
          getColumnProperties({
            accessor: "pass_percentage"
          }),
          getColumnProperties({
            accessor: "att_freekick_total"
          }),
          getColumnProperties({
            accessor: "corner_taken"
          }),
          getColumnProperties({
            accessor: "goal_assist",
            disableSortBy: false
          }),
          getColumnProperties({
            accessor: "total_att_assist"
          }),
          getColumnProperties({
            accessor: "accurate_long_balls"
          }),
          getColumnProperties({
            accessor: "long_ball_percentage"
          }),
        ]
      };
    case statTypes.clubStatsPassingAllYears:
      return {
        Header: "passing",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          {
            Header: t("table_club"),
            accessor: "club",
            sortType: (a, b, accessorProperty) => sortClubNames(a, b, accessorProperty),
            Cell: (props) => {
              const displayName = props.row.original.club?.shortName || props.row.original.club?.fullName;
              const abbreviation = props.row.original.club?.abbreviation;
              return props.value || props.value === 0
                ? (
                  <>
                    {
                      props.row.original?.club
                        ?
                        (
                          <span className="mls-o-table__club-logo">
                            <ClubLogo club={props.row.original.club} element_position="club-stats-passing-all-years" />
                          </span>
                        )
                        : null
                    }
                    <ClubName name={displayName} abbreviation={abbreviation} slug={props.row.original.club?.slug} />
                  </>
                )
                : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
            }
          },
          getColumnProperties({
            accessor: "accurate_pass",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "total_pass",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "pass_percentage",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "accurate_fwd_zone_pass",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "accurate_back_zone_pass",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "goal_assist",
            disableSortBy: false
          }),
          getColumnProperties({
            accessor: "total_att_assist",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "accurate_long_balls",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "long_ball_percentage",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
        ]
      };
    case statTypes.clubStatsGeneralNoStandings:
    case statTypes.clubStatsGeneralPostseason:
      return {
        Header: "club-general",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          {
            Header: t("table_club"),
            accessor: "club",
            sortType: (a, b, accessorProperty) => sortClubNames(a, b, accessorProperty),
            Cell: (props) => {
              const displayName = props.row.original.club?.shortName || props.row.original.club?.fullName;
              const abbreviation = props.row.original.club?.abbreviation;

              return props.value || props.value === 0
                ? (
                  <>
                    {
                      props.row.original?.club
                        ?
                        (
                          <span className="mls-o-table__club-logo">
                            <ClubLogo club={props.row.original.club} element_position="club-stats-general" />
                          </span>
                        )
                        : null
                    }
                    <ClubName name={displayName} abbreviation={abbreviation} slug={props.row.original.club?.slug} />
                  </>
                )
                : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
            }
          },
          getColumnProperties({
            Header: getColumnHeader("games_played"),
            accessor: "games_played"
          }),
          getColumnProperties({
            Header: getColumnHeader("goals"),
            accessor: "goals"
          }),
          getColumnProperties({
            Header: getColumnHeader("att_pen_taken"),
            accessor: "att_pen_taken"
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            disableSortBy: true
          }),
          getColumnProperties({
            Header: getColumnHeader("penalty_conceded_pkga"),
            accessor: "penalty_conceded"
          }),
          getColumnProperties({
            accessor: "total_goal_differential"
          }),
          getColumnProperties({
            Header: getColumnHeader("yellow_card"),
            accessor: "yellow_card"
          }),

          getColumnProperties({
            Header: getColumnHeader("red_card"),
            accessor: "total_red_card"
          })
        ]
      };
    case statTypes.clubStatsGeneralAllYears:
      return {
        Header: "club-general",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          {
            Header: t("table_club"),
            accessor: "club",
            sortType: (a, b, accessorProperty) => sortClubNames(a, b, accessorProperty),
            Cell: (props) => {
              const displayName = props.row.original?.club?.shortName || props.row.original?.club?.fullName;
              const abbreviation = props.row.original?.club?.abbreviation;

              return props.value || props.value === 0
                ? (
                  <>
                    {
                      props.row.original?.club
                        ?
                        (
                          <span className="mls-o-table__club-logo">
                            <ClubLogo club={props.row.original.club} element_position="club-stats-general-all-years" />
                          </span>
                        )
                        : null
                    }
                    <ClubName name={displayName} abbreviation={abbreviation} slug={props.row.original.club?.slug} />
                  </>
                )
                : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
            }
          },
          getColumnProperties({
            Header: getColumnHeader("games_played"),
            accessor: "games_played",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "total_points",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "total_wins",
            disableSortBy: true,
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "total_losses",
            disableSortBy: true,
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "total_draws",
            disableSortBy: true,
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            Header: getColumnHeader("goals"),
            accessor: "goals",
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "total_goal_differential"
          }),
          getColumnProperties({
            Header: getColumnHeader("att_pen_taken"),
            accessor: "att_pen_taken"
          }),
          getColumnProperties({
            Header: getColumnHeader("penalty_conceded_pkga"),
            accessor: "penalty_conceded",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "fk_foul_lost",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "fk_foul_won",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "total_offside"
          }),
          getColumnProperties({
            Header: getColumnHeader("yellow_card"),
            accessor: "yellow_card",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            Header: getColumnHeader("red_card"),
            accessor: "total_red_card",
            customCellFn: fallbackClubStatsGeneralStandings
          })
        ]
      };
    case statTypes.clubStatsGeneral:
      return {
        Header: "club-general",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          {
            Header: t("table_club"),
            accessor: "club",
            sortType: (a, b, accessorProperty) => sortClubNames(a, b, accessorProperty),
            Cell: (props) => {
              const displayName = props.row.original?.club?.shortName || props.row.original?.club?.fullName;
              const abbreviation = props.row.original?.club?.abbreviation;

              return props.value || props.value === 0
                ? (
                  <>
                    {
                      props.row.original?.club
                        ?
                        (
                          <span className="mls-o-table__club-logo">
                            <ClubLogo club={props.row.original.club} element_position="club-stats-general" />
                          </span>
                        )
                        : null
                    }
                    <ClubName name={displayName} abbreviation={abbreviation} slug={props.row.original.club?.slug} />
                  </>
                )
                : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
            }
          },
          getColumnProperties({
            Header: getColumnHeader("games_played"),
            accessor: "games_played"
          }),
          getColumnProperties({
            accessor: "total_points",
          }),
          getColumnProperties({
            accessor: "total_wins",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "total_losses",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "total_draws",
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: getColumnHeader("goals"),
            accessor: "goals"
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "total_goal_differential"
          }),
          getColumnProperties({
            Header: getColumnHeader("att_pen_taken"),
            accessor: "att_pen_taken"
          }),
          getColumnProperties({
            Header: getColumnHeader("penalty_conceded_pkga"),
            accessor: "penalty_conceded"
          }),
          getColumnProperties({
            accessor: "fk_foul_lost"
          }),
          getColumnProperties({
            accessor: "fk_foul_won"
          }),
          getColumnProperties({
            accessor: "total_offside"
          }),
          getColumnProperties({
            Header: getColumnHeader("yellow_card"),
            accessor: "yellow_card"
          }),
          getColumnProperties({
            Header: getColumnHeader("red_card"),
            accessor: "total_red_card"
          })
        ]
      };
    case statTypes.clubStatsAttackingPostseason:
    case statTypes.clubStatsAttacking:
      return {
        Header: "club-attacking",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          {
            Header: t("table_club"),
            accessor: "club",
            sortType: (a, b, accessorProperty) => sortClubNames(a, b, accessorProperty),
            Cell: (props) => {
              const displayName = props.row.original.club?.shortName || props.row.original.club?.fullName;
              const abbreviation = props.row.original.club?.abbreviation;

              return props.value || props.value === 0
                ? (
                  <>
                    {
                      props.row.original?.club
                        ?
                        (
                          <span className="mls-o-table__club-logo">
                            <ClubLogo club={props.row.original.club} element_position="club-stats-attacking" />
                          </span>
                        )
                        : null
                    }
                    <ClubName name={displayName} abbreviation={abbreviation} slug={props.row.original.club?.slug} />
                  </>
                )
                : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
            }
          },
          getColumnProperties({
            accessor: "goals"
          }),
          getColumnProperties({
            accessor: "total_att_assist"
          }),
          getColumnProperties({
            accessor: "corner_taken",
            sortInverted: false
          }),
          getColumnProperties({
            accessor: "expected_goals"
          }),
          getColumnProperties({
            accessor: "att_pen_goal"
          }),
          getColumnProperties({
            accessor: "att_pen_taken"
          }),
          getColumnProperties({
            accessor: "att_pen_percentage"
          }),
          getColumnProperties({
            accessor: "total_scoring_att"
          }),
          getColumnProperties({
            accessor: "ontarget_scoring_att"
          }),
          getColumnProperties({
            accessor: "accurate_shooting_per"
          }),
          getColumnProperties({
            accessor: "goal_assist",
            disableSortBy: false,
          }),
        ]
      };
    case statTypes.clubStatsAttackingAllYears:
      return {
        Header: "club-attacking",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          {
            Header: t("table_club"),
            accessor: "club",
            sortType: (a, b, accessorProperty) => sortClubNames(a, b, accessorProperty),
            Cell: (props) => {
              const displayName = props.row.original.club?.shortName || props.row.original.club?.fullName;
              const abbreviation = props.row.original.club?.abbreviation;

              return props.value || props.value === 0
                ? (
                  <>
                    {
                      props.row.original?.club
                        ?
                        (
                          <span className="mls-o-table__club-logo">
                            <ClubLogo club={props.row.original.club} element_position="club-stats-attacking-all-years" />
                          </span>
                        )
                        : null
                    }
                    <ClubName name={displayName} abbreviation={abbreviation} slug={props.row.original.club?.slug} />
                  </>
                )
                : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
            }
          },
          getColumnProperties({
            accessor: "goals",
          }),
          getColumnProperties({
            accessor: "att_ibox_goal",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "att_obox_goal",
            customCellFn: fallbackClubStatsGeneralStandings,
            sortInverted: false
          }),
          getColumnProperties({
            accessor: "att_hd_goal",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "att_pen_goal"
          }),
          getColumnProperties({
            accessor: "att_pen_taken"
          }),
          getColumnProperties({
            accessor: "att_pen_percentage"
          }),
          getColumnProperties({
            accessor: "total_scoring_att"
          }),
          getColumnProperties({
            accessor: "ontarget_scoring_att"
          }),
          getColumnProperties({
            accessor: "accurate_shooting_per",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "goal_assist",
            disableSortBy: false
          }),
          getColumnProperties({
            accessor: "corner_taken",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "total_att_assist",
            customCellFn: fallbackClubStatsGeneralStandings
          })
        ]
      };
    case statTypes.statsAttacking:
      return {
        Header: "attacking",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            Header: t("table_club"),
            accessor: "club",
            disableSortBy: true,
            customCellFn: fallbackClubTableData
          }),
          getColumnProperties({
            accessor: "games_played",
            customSortFn: null
          }),
          getColumnProperties({
            accessor: "game_started",
            disableSortBy: true
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "goals"
          }),
          getColumnProperties({
            accessor: "att_lf_total",
            t
          }),
          getColumnProperties({
            accessor: "att_rf_total",
            sortInverted: false
          }),
          getColumnProperties({
            accessor: "goals_90",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "att_hd_total"
          }),
          getColumnProperties({
            accessor: "att_pen_goal"
          }),
          getColumnProperties({
            accessor: "att_pen_taken"
          }),
          getColumnProperties({
            accessor: "expected_goals",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "total_scoring_att"
          }),
          getColumnProperties({
            accessor: "ontarget_scoring_att"
          }),
          getColumnProperties({
            accessor: "accurate_shooting_per",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "shot_conversion",
            disableSortBy: true
          }),
          //getColumnProperties({
          //  Header: "DRBs",
          //  accessor: "total_contest"
          //}),
          //getColumnProperties({
          //  Header: "DRB%",
          //  accessor: "dribble_percentage_match_hub", disableSortBy: true
          //}),
          getColumnProperties({
            accessor: "total_offside"
          }),
          getColumnProperties({
            accessor: "corner_taken", disableSortBy: true
          }),
          getColumnProperties({
            accessor: "goals_assists", disableSortBy: true
          }),
        ]
      };
    case statTypes.matchHubPlayerStats:
      return {
        Header: `goalkeeper`,
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "goals"
          }),
          getColumnProperties({
            accessor: "expected_goals",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "total_scoring_att",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "ontarget_scoring_att",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "accurate_pass_per_match_hub",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "goal_assist",
            disableSortBy: false
          }),
          getColumnProperties({
            accessor: "accurate_pass"
          }),
          getColumnProperties({
            accessor: "accurate_cross",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "corner_taken",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "total_att_assist"
          }),
          getColumnProperties({
            accessor: "aerial_duel",
          }),
          getColumnProperties({
            accessor: "aerial_duel_per",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "fouls",
          }),
          getColumnProperties({
            accessor: "was_fouled",
          }),
          getColumnProperties({
            accessor: "total_offside"
          }),
          getColumnProperties({
            Header: getColumnHeader("yellow_card"),
            accessor: "yellow_card"
          }),

          getColumnProperties({
            Header: getColumnHeader("red_card"),
            accessor: "total_red_card"
          }),
        ]
      };
    case statTypes.matchHubGoalkeeperStats:
      return {
        Header: "match-hub-goalkeeper",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "saves",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "expected_goals_conceded",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "successful_passes",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "accurate_pass_per_match_hub",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "keeper_throws",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "total_long_balls",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "total_launches",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "goal_kicks",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "fouls",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "was_fouled",
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: getColumnHeader("yellow_card"),
            accessor: "yellow_card",
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: getColumnHeader("red_card"),
            accessor: "total_red_card",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "lost_corners",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "punches",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "assists",
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: getColumnHeader("goals"),
            accessor: "goals",
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "total_scoring_att",
            disableSortBy: true,
          })
        ]
      };
    case statTypes.playersMatchLogGeneral:
      return {
        Header: "players-match-log",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_date"),
            accessor: "date",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: t("table_competition"),
            accessor: "name",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: t("table_match"),
            accessor: "match",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_result"),
            accessor: "results",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_appearance"),
            accessor: "status",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: getColumnHeader("goals"),
            accessor: "goals",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: getColumnHeader("expected_goals"),
            accessor: "expected_goals",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: getColumnHeader("goal_assist"),
            accessor: "goal_assist",
            disableSortBy: false,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            accessor: "total_scoring_att",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            accessor: "ontarget_scoring_att",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            accessor: "accurate_pass_per",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "fouls",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            accessor: "was_fouled",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            accessor: "total_offside",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            accessor: "yellow_card",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            accessor: "total_red_card",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          })
        ]
      };
    case statTypes.playersMatchLogAttacking:
      return {
        Header: "players-match-log",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_date"),
            accessor: "date",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: t("table_competition"),
            accessor: "name",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: t("table_match"),
            accessor: "match",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_appearance"),
            accessor: "status",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: getColumnHeader("goals"),
            accessor: "goals",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "expected_goals",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "expected_goals_minus_goals",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "att_lf_total",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "att_rf_total",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "att_hd_total",
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "att_pen_goal",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "pka",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "total_scoring_att",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "ontarget_scoring_att",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "accurate_shooting_per",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "goal_assist",
            disableSortBy: false,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "corner_taken",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "total_offside",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "total_att_assist",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "goals_assists",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "att_freekick_total",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
        ]
      };
    case statTypes.playersMatchLogPassing:
      return {
        Header: "players-match-log",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_date"),
            accessor: "date",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: t("table_competition"),
            accessor: "name",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: t("table_match"),
            accessor: "match",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_appearance"),
            accessor: "status",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            accessor: "accurate_pass",
            disableSortBy: false,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "total_pass",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          // getColumnProperties({
          //   disableSortBy: true,
          //   accessor: "accurate_pass_per",
          //   customCellFn: fallbackMatchLogData
          // }),
          // getColumnProperties({
          //   accessor: "accurate_fwd_zone_pass",
          //   customCellFn: fallbackMatchLogData
          // }),
          getColumnProperties({
            accessor: "total_att_assist",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "accurate_cross",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "goal_assist",
            customCellFn: fallbackMatchLogData,
            disableSortBy: false,
          }),
          getColumnProperties({
            accessor: "corner_taken",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "accurate_cross_per",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            Header: getColumnHeader("att_freekick_total"),
            accessor: "att_freekick_total",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "total_cross",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
        ]
      };
    case statTypes.playersMatchLogDefending:
      return {
        Header: "players-match-log",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_date"),
            accessor: "date",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: t("table_competition"),
            accessor: "name",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: t("table_match"),
            accessor: "match",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_appearance"),
            accessor: "status",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "aerial_duel",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "aerial_duel_per",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "total_clearance",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "fk_foul_lost",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "touches",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
        ]
      };
    case statTypes.playersMatchLogGoalKeeping:
      return {
        Header: "players-match-log",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_date"),
            accessor: "date",
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: t("table_competition"),
            accessor: "name",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData,
          }),
          getColumnProperties({
            Header: t("table_match"),
            accessor: "match",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_appearance"),
            accessor: "status",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "expected_goals_conceded",
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "goals_against_expected_goals_against",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "gk_shots_against",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "saves",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "save_percentage",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "penalty_faced",
            customCellFn: fallbackMatchLogData,
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "penalty_save",
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            disableSortBy: true,
            accessor: "penalty_kick_save_percentage",
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "accurate_pass_per",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "long_ball_percentage",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
          getColumnProperties({
            accessor: "clearances",
            disableSortBy: true,
            customCellFn: fallbackMatchLogData
          }),
        ]
      };
    case statTypes.playersCareerStatsGeneral:
      return {
        Header: "players-match-log",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_year"),
            accessor: "date",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            Header: t("table_club"),
            accessor: "name",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsDataText,
          }),
          getColumnProperties({
            Header: getColumnHeader("games_played"),
            accessor: "games_played",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "game_started",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            Header: getColumnHeader("goals"),
            accessor: "goals",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            Header: getColumnHeader("goal_assist"),
            accessor: "goal_assist",
            disableSortBy: false,
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "total_scoring_att",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "ontarget_scoring_att",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "accurate_pass_per",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "total_offside",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "fk_foul_lost",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "fk_foul_won",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "yellow_card",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "total_red_card",
            customCellFn: fallbackCareerStatsData,
          })
        ]
      };
    case statTypes.playersCareerStatsPassing:
      return {
        Header: "players-match-log",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_year"),
            accessor: "date",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            Header: t("table_club"),
            accessor: "name",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsDataText,
          }),
          getColumnProperties({
            Header: getColumnHeader("games_played"),
            accessor: "games_played",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "game_started",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "accurate_pass",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "total_pass",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            disableSortBy: true,
            accessor: "accurate_pass_per",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "total_att_assist",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "corner_taken",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "accurate_cross",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "goal_assist",
            disableSortBy: false,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            Header: getColumnHeader("accurate_long_balls"),
            accessor: "accurate_long_balls",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "successful_short_pass",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "accurate_cross_per",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "att_freekick_total",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true
          }),
        ]
      };
    case statTypes.playersCareerStatsAttacking:
      return {
        Header: "players-match-log",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_year"),
            accessor: "date",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            Header: t("table_club"),
            accessor: "name",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsDataText,
          }),
          getColumnProperties({
            Header: getColumnHeader("games_played"),
            accessor: "games_played",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "game_started",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            Header: getColumnHeader("goals"),
            accessor: "goals",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "att_rf_total",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "att_lf_total",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "att_hd_total",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "att_pen_goal",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "att_pen_taken",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "expected_goals",
          }),
          getColumnProperties({
            accessor: "total_scoring_att",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "ontarget_scoring_att",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "accurate_shooting_per",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "goal_assist",
            disableSortBy: false,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "total_att_assist",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "corner_taken",
            customCellFn: fallbackCareerStatsData
          }),
        ]
      };
    case statTypes.playersCareerStatsDefending:
      return {
        Header: "players-match-log",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_year"),
            accessor: "date",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            Header: t("table_club"),
            accessor: "name",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsDataText,
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true
          }),
          getColumnProperties({
            Header: getColumnHeader("games_played"),
            accessor: "games_played",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "game_started",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "aerial_duel",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "aerial_duel_per",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "total_clearance",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "fk_foul_lost",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "interception",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true
          }),
        ]
      };
    case statTypes.playersCareerStatsGoalKeeping:
      return {
        Header: "players-match-log",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_year"),
            accessor: "date",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            Header: t("table_club"),
            accessor: "name",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsDataText,
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true
          }),
          getColumnProperties({
            Header: getColumnHeader("games_played"),
            accessor: "games_played",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "game_started",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played",
            customCellFn: fallbackCareerStatsData,
          }),
          getColumnProperties({
            accessor: "clean_sheet",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "expected_goals_conceded",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "goals_against_expected_goals_against",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "saves",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "save_percentage",
            customCellFn: fallbackCareerStatsData,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "penalty_faced",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "penalty_save",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            disableSortBy: true,
            accessor: "penalty_kick_save_percentage",
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "accurate_pass_per",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "long_ball_percentage",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsData
          }),
          getColumnProperties({
            accessor: "clearances",
            disableSortBy: true,
            customCellFn: fallbackCareerStatsData
          }),
        ]
      };
    case statTypes.activeRoster:
      return {
        Header: "active-roster",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            customCellFn: fallbackRosterPlayerTableData,
            customSortFn: sortByLastName
          }),
          getColumnProperties({
            Header: t("table_jersey_number"),
            accessor: "jerseyNumber",
            customCellFn: fallbackRosterData,
          }),
          getColumnProperties({
            Header: t("table_position"),
            accessor: "position",
            customCellFn: fallbackRosterData,
          }),
          getColumnProperties({
            Header: t("table_roster_category"),
            accessor: "rosterCategory",
            customCellFn: fallbackRosterData,
          }),
          getColumnProperties({
            Header: t("table_player_category"),
            accessor: "playerCategories",
            customCellFn: fallbackPlayerCategoriesTableData,
          }),
          getColumnProperties({
            Header: t("table_player_status"),
            accessor: "playerStatusList",
            customCellFn: fallbackPlayerStatusTableData,
          }),
        ]
      };
    case statTypes.activeRosterFiltered:
      return {
        Header: "active-roster",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            customCellFn: fallbackRosterPlayerTableData,
            customSortFn: sortByLastName
          }),
          getColumnProperties({
            Header: t("table_jersey_number"),
            accessor: "jerseyNumber",
            customCellFn: fallbackRosterData,
          }),
          getColumnProperties({
            Header: t("table_position"),
            accessor: "position",
            customCellFn: fallbackRosterData,
          })
        ]
      };
    case statTypes.playersHubRoster:
      return {
        Header: "players-hub-roster",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            customCellFn: fallbackRosterPlayerTableData,
            customSortFn: sortByLastName
          }),
          getColumnProperties({
            Header: t("table_position"),
            accessor: "position",
            customCellFn: fallbackRosterData,
          }),
        ]
      };
    case statTypes.playersCareerStats:
      return {
        Header: "player-career-stats",
        accessor: 'player-type',
        columns: [
          getColumnProperties({
            Header: t("table_year"),
            accessor: "year",
          }),
          getColumnProperties({
            Header: t("table_club"),
            accessor: "name",
            customCellFn: fallbackCareerData,
          }),
          getColumnProperties({
            accessor: "gamesPlayed",
            customCellFn: fallbackCareerData,
          }),
          getColumnProperties({
            accessor: "gameStarted",
            customCellFn: fallbackCareerData,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "minsPlayed",
            customCellFn: fallbackCareerData,
          }),
          getColumnProperties({
            accessor: "yellowCard",
            customCellFn: fallbackCareerData,
          }),
          getColumnProperties({
            accessor: "redCard",
            customCellFn: fallbackCareerData,
          }),
        ]
      };
    case statTypes.statsGeneralAllYears:
      return {
        Header: "general",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            accessor: "games_played",
            customSortFn: null
          }),
          getColumnProperties({
            accessor: "game_started",
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "total_sub_on",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "goals",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "accurate_pass_per",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "goal_assist",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "total_scoring_att",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "ontarget_scoring_att",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "total_att_assist",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "won_tackle"
          }),
          getColumnProperties({
            accessor: "fk_foul_lost",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "fk_foul_won",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "total_offside",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            Header: getColumnHeader("yellow_card"),
            accessor: "yellow_card"
          }),

          getColumnProperties({
            accessor: "total_red_card"
          })
        ]
      };
    case statTypes.statsAttackingAllYears:
      return {
        Header: "attacking",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            accessor: "games_played",
            customSortFn: null
          }),
          getColumnProperties({
            accessor: "game_started",
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "goals",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "goals_90",
            disableSortBy: true,
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "att_pen_goal",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "att_pen_taken",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "att_pen_percentage",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "total_scoring_att",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "ontarget_scoring_att",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "shot_conversion",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "goals_assists", disableSortBy: true
          }),
        ]
      };
    case statTypes.statsPassingAllYears:
      return {
        Header: t("passing"),
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            accessor: "games_played",
            customSortFn: null
          }),
          getColumnProperties({
            accessor: "game_started",
            disableSortBy: true
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "accurate_pass",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "total_pass",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "accurate_pass_per",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "accurate_fwd_zone_pass",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "accurate_back_zone_pass",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "accurate_cross"
          }),
          getColumnProperties({
            accessor: "goal_assist",
            disableSortBy: false,
          }),
          getColumnProperties({
            accessor: "assist_90"
          }),
          getColumnProperties({
            accessor: "total_att_assist",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            Header: getColumnHeader("accurate_long_balls"),
            accessor: "accurate_long_balls",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "successful_short_pass",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "launches_attempted",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "turnover",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
        ]
      };
    case statTypes.statsDefendingAllYears:
      return {
        Header: "defending",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            accessor: "games_played",
            customSortFn: null
          }),
          getColumnProperties({
            accessor: "game_started",
            disableSortBy: true
          }),
          getColumnProperties({
            Header: t("mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "clean_sheet",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true,
          }),
          getColumnProperties({
            accessor: "headed_duel_percentage",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "duel",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "duel_percentage",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
          //getColumnProperties({
          //  Header: "Duel%",
          //  accessor: "ground_duel_percentage", disableSortBy: true
          //}),
          getColumnProperties({
            accessor: "interception",
            disableSortBy: true
          }),
        ]
      };
    case statTypes.statsGoalKeepingAllYears:
      return {
        Header: "goalkeeping",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            accessor: "games_played",
            customSortFn: null
          }),
          getColumnProperties({
            accessor: "game_started",
            disableSortBy: true
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "goals_conceded",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "goals_allowed_average",
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "saves",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "save_percentage_gk",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "penalty_faced",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "pen_goals_conceded",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "accurate_pass_per",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "long_ball_percentage",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true
          }),
          getColumnProperties({
            accessor: "total_clearance",
            customCellFn: fallbackClubStatsGeneralStandings
          }),
          getColumnProperties({
            accessor: "clean_sheet",
            customCellFn: fallbackClubStatsGeneralStandings,
            disableSortBy: true,
          }),
        ]
      };
    case statTypes.statsGeneral:
    default:
      return {
        Header: "general",
        accessor: 'stats-type',
        disableSortBy: true,
        columns: [
          getColumnProperties({
            Header: t("table_player"),
            accessor: "player",
            disableSortBy: true,
            customCellFn: fallbackPlayerTableData
          }),
          getColumnProperties({
            Header: t("table_club"),
            accessor: "club",
            disableSortBy: true,
            customCellFn: fallbackClubTableData
          }),
          getColumnProperties({
            accessor: "games_played",
            customSortFn: null
          }),
          getColumnProperties({
            accessor: "game_started",
            disableSortBy: true,
          }),
          getColumnProperties({
            Header: t("table_mins"),
            accessor: "mins_played"
          }),
          getColumnProperties({
            accessor: "total_sub_on"
          }),
          getColumnProperties({
            accessor: "goals"
          }),
          getColumnProperties({
            accessor: "accurate_pass_per"
          }),
          getColumnProperties({
            accessor: "goal_assist",
            disableSortBy: false,
          }),
          getColumnProperties({
            accessor: "total_scoring_att"
          }),
          getColumnProperties({
            accessor: "ontarget_scoring_att"
          }),
          getColumnProperties({
            accessor: "total_att_assist"
          }),
          getColumnProperties({
            accessor: "expected_goals"
          }),
          getColumnProperties({
            accessor: "fk_foul_lost",
          }),
          getColumnProperties({
            accessor: "fk_foul_won",
          }),
          getColumnProperties({
            accessor: "total_offside"
          }),
          getColumnProperties({
            Header: getColumnHeader("yellow_card"),
            accessor: "yellow_card"
          }),

          getColumnProperties({
            accessor: "total_red_card"
          })
        ]
      };
  }
}

export function addTotalShootoutWinsColumn(currentCols) {
  const colIdx = 8;

  if (!currentCols || currentCols.length === 0
    || !currentCols[0]?.columns || currentCols[0]?.columns?.length < colIdx + 1) {
    return;
  }

  const colDefinition = getColumnProperties({
    accessor: "shootout_wins",
    sortInverted: false,
    customCellFn: fallbackTableDataLoaderString
  });

  currentCols[0]?.columns?.splice(colIdx, 0, colDefinition);
}

function fallbackPlayerTableData(props) {
  const { highQuality } = props.row.original?.player?.image?.templateUrl ? formatImageUrl(props.row.original.player.image.templateUrl, { format: "w_60,c_scale", extension: "f_png" }) : {};

  const playerDisplayName = props.row.original.player?.knownName ? props.row.original.player?.knownName : `${getFirstCharacter(props.row.original.player?.firstName)}. ${props.row.original.player?.lastName}`;

  const handlePlayerClick = (e) => {
    e.preventDefault();
    const href = e.currentTarget.href;
    const { player } = props.row.original;

    trackPlayerClick({
      playerData: {
        player_id: player?.player_id,
        player_name: `${player?.firstName} ${player?.lastName}`
      },
      element_name: playerDisplayName,
      element_type: "player",
      element_position: "player-stats-table",
    });

    setTimeout(() => {
      window.location.href = href;
    }, 100);
  };

  return props.value ? (
    <a className="mls-o-table__href" href={props.row.original.player?.playerUrl} alt={playerDisplayName} onClick={handlePlayerClick}>
      <div className="mls-o-table__player">
        <div className="mls-o-table__player-image">
          <Picture
            loadingSrc={"/assets/images/Fallback-Headshot.png"}
            fallbackSrc={"/assets/images/Fallback-Headshot.png"}
            src={highQuality}
            title={props.row.original.player?.knownName} />
        </div>
        <div className="mls-o-table__player-name">
          <div
            className="short-name">{playerDisplayName}</div>
        </div>
      </div>
    </a>
  ) : (
    <div className="mls-o-table__player">
      <div className="mls-o-table__player-image">
        <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-image" />
      </div>
      <div className="mls-o-table__player-name">
        <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell mls-o-loading--70" />
      </div>
    </div>
  );
}

function fallbackPlayerCategoriesTableData(props) {
  const playerCategories = props.row.original?.playerCategories || "";
  return !!playerCategories ? (
    <p>{playerCategories}</p>
  ) : (
    <></>
  );
}

function fallbackPlayerStatusTableData(props) {
  const playerStatus = props.row.original?.playerStatusList || "";
  return !!playerStatus ? (
    <p>{playerStatus}</p>
  ) : (
    <></>
  );
}

function fallbackRosterPlayerTableData(props) {
  const { highQuality } = props.row.original?.thumbnail?.templateUrl ? formatImageUrl(props.row.original?.thumbnail?.templateUrl, { format: "w_60,c_scale", extension: "f_png" }) : {};

  const playerDisplayName = props.row.original?.knownName ? props.row.original?.knownName : `${getFirstCharacter(props.row.original?.firstName)}. ${props.row.original?.lastName}`;

  const handlePlayerClick = (e) => {
    e.preventDefault();
    const href = e.currentTarget.href;
    const { player } = props.row.original;

    trackPlayerClick({
      playerData: {
        player_id: player?.player_id,
        player_name: `${player?.firstName} ${player?.lastName}`
      },
      element_name: playerDisplayName,
      element_type: "player",
      element_position: "player-roster-table",
    });

    setTimeout(() => {
      window.location.href = href;
    }, 100);
  };
  return playerDisplayName?.replace(/[.]/g, "").trim().length > 0 ? (
    <>
      <a className="mls-o-table__href" href={props.row.original?.playerUrl} onClick={handlePlayerClick}>
        <div className="mls-o-table__player">
          <div className="mls-o-table__player-image">
            {
              highQuality ?
                <Picture
                  loadingSrc={"/assets/images/Fallback-Headshot.png"}
                  fallbackSrc={"/assets/images/Fallback-Headshot.png"}
                  src={highQuality}
                  title={playerDisplayName} />
                :
                <Picture
                  loadingSrc={"/assets/images/Fallback-Headshot.png"}
                  fallbackSrc={"/assets/images/Fallback-Headshot.png"}
                  src={"/assets/images/Fallback-Headshot.png"}
                  title={playerDisplayName} />
            }
          </div>
          <div className="mls-o-table__player-name">
            <div
              className="short-name">{playerDisplayName}</div>
          </div>
        </div>
      </a>
    </>
  ) : (
    <></>
  );
}

function fallbackClubTableData(props) {
  return props.value
    ? <span className="mls-o-table__abbreviation">{props.row.original.club?.abbreviation}</span>
    : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell mls-o-loading--70" />;
}

function fallbackMatchLogData(props) {
  if (props.value === null) {
    return <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell mls-o-loading--70" />;
  }

  if (props.value) {
    return props.value;
  } else {
    return 0;
  }
}

function fallbackClubStatsGeneralStandings(props) {
  return props.value || props.value === 0 || props.value === undefined
    ? props?.value ? round(props.value) : "-"
    : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
}

function fallbackCareerStatsData(props) {
  if (props.value === null) {
    return <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell mls-o-loading--70" />;
  }

  if (props.value) {
    return props.value;
  } else {
    return 0;
  }
}

function fallbackCareerStatsDataText(props) {
  if (props.value === null) {
    return <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell mls-o-loading--70" />;
  }

  if (props.value) {
    return props.value;
  } else {
    return "";
  }
}

function fallbackRosterData(props) {
  return props.value ? props.value : "";
}

function fallbackCareerData(props) {
  return props.value ? props.value : 0;
}

function fallbackTableDataLoaderString(props) {
  return props.value || props.value === 0
    ? props.value
    : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
}

function fallbackTableDataLoaderInteger(props) {
  return props.value || props.value === 0
    ? round(props.value)
    : <div className="mls-o-loading mls-o-loading--glow mls-o-loading--table-cell" />;
}

function getColumnProperties({ accessor, Header = getColumnHeader(accessor), customCellFn = fallbackTableDataLoaderInteger, customSortFn = customSort, disableSortBy = false, sortInverted = false, sortDescFirst = true } = {}) {
  let columnObj = {
    Header,
    accessor,
    sortInverted,
    disableSortBy,
    sortDescFirst
  };

  if (customSortFn && !disableSortBy) {
    columnObj = { ...columnObj, sortType: (a, b, accessorProperty) => customSortFn(a, b, accessorProperty) };
  }

  if (customCellFn) {
    columnObj = { ...columnObj, Cell: (props) => customCellFn(props) };
  }

  return columnObj;
}

function getColumnHeader(accessor) {
  const { t } = useReactAppState() || {};
  return (
    <>
      <span role="button" aria-label={t(`th-${accessor}`)} data-tip={t(`th-${accessor}`)}>
        {t(`th-${accessor}.abbr`, { keySeparator: false })}
      </span>
      <ReactTooltip />
    </>
  );
}
